import type { CustomerOrder, Workspace } from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { NotFoundPage } from '@/widgets/not-found';
import { useWorkspaceOrders } from '../../entities/order';
import { useOrgContext } from '../../entities/org';
import {
  CUSTOMER_STATUS_TONE,
  customerStatus,
  isOpenDraft,
  useProjects,
} from '../../entities/project';
import { DiscardDraftButton } from '../../features/order/discard-draft';
import { StartOrderButton } from '../../features/order/start-order';
import { useFormat } from '../../shared/i18n/format';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '../../shared/ui/breadcrumb';
import { Button } from '../../shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../shared/ui/card';
import { EmptyState } from '../../shared/ui/empty-state';
import { ErrorState } from '../../shared/ui/error-state';
import { Notice } from '../../shared/ui/notice';
import { PageLayout } from '../../shared/ui/page-layout';
import { Skeleton } from '../../shared/ui/skeleton';
import { StatusBadge } from '../../shared/ui/status-badge';
import { TopBarBreadcrumb } from '../../widgets/app-shell';

const gridClass = 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3';

/**
 * `/o/:orgSlug/w/:workspaceSlug`: one workspace. **New order** starts an order; drafts that have not been submitted
 * are listed under "Continue your order", for everyone who can see the workspace (a draft follows grants, not its
 * creator); everything else is the project list. `RequireWorkspace` has resolved the slug.
 */
export function ProjectsPage() {
  const { workspaceSlug = '' } = useParams();
  const workspace = useOrgContext().workspaceBySlug(workspaceSlug);
  return workspace ? <WorkspaceProjects workspace={workspace} /> : <NotFoundPage />;
}

function WorkspaceProjects({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation(['project', 'workspace', 'order']);
  useDocumentTitle(workspace.name);
  const navigate = useNavigate();
  const { formatDate, formatDateTime } = useFormat();
  const { org, canIn } = useOrgContext();
  const base = `/o/${org.slug}/w/${workspace.slug}`;
  const archived = workspace.archived_at !== null;
  // What to show; start_order refuses anyway without both permissions or in an archived workspace.
  const canStart =
    canIn(workspace.id, 'orders.create') && canIn(workspace.id, 'projects.create') && !archived;
  const canDiscard = canIn(workspace.id, 'projects.edit') && !archived;
  const projects = useProjects(workspace.id);
  const orders = useWorkspaceOrders(workspace.id);
  const orderOf = new Map<string, CustomerOrder>(
    (orders.data ?? []).map((order) => [order.project_id, order]),
  );
  // Until the orders are in, nothing can be told apart: a draft from an order awaiting payment.
  const ready = projects.data && orders.data ? projects.data : null;
  const drafts = (ready ?? []).filter((p) => isOpenDraft(p, orderOf.get(p.id) ?? null));
  const listed = (ready ?? []).filter((p) => !isOpenDraft(p, orderOf.get(p.id) ?? null));
  const failed = projects.isError ? projects : orders.isError ? orders : null;

  return (
    <PageLayout
      title={workspace.name}
      description={t('list.description')}
      actions={
        canStart ? (
          <StartOrderButton
            workspaceId={workspace.id}
            onStarted={(projectId) => navigate(`${base}/projects/${projectId}`)}
          />
        ) : undefined
      }
    >
      <TopBarBreadcrumb>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>{t('list.breadcrumb')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </TopBarBreadcrumb>

      {archived ? <Notice tone="warning">{t('workspace:archivedBanner')}</Notice> : null}

      {drafts.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle asChild className="font-sans">
              <h2>{t('order:drafts.title')}</h2>
            </CardTitle>
            <CardDescription>{t('order:drafts.help')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="grid divide-y">
              {drafts.map((draft) => {
                const name = draft.name === 'Untitled' ? t('order:drafts.untitled') : draft.name;
                return (
                  <li
                    key={draft.id}
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3 first:pt-0 last:pb-0"
                  >
                    <div className="grid min-w-0 gap-0.5">
                      <p className="font-bold break-words text-heading">{name}</p>
                      <p className="text-sm text-muted-foreground">
                        {draft.product ? t(`order:products.${draft.product}`) : null}
                        {' · '}
                        {t('order:drafts.edited', { date: formatDateTime(draft.draft_touched_at) })}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {canDiscard ? <DiscardDraftButton projectId={draft.id} name={name} /> : null}
                      <Button asChild variant="secondary" size="sm">
                        <Link
                          to={`${base}/projects/${draft.id}`}
                          aria-label={t('order:drafts.continueNamed', { name })}
                        >
                          {t('order:drafts.continue')}
                        </Link>
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {!ready && !failed ? (
        <section aria-busy="true" aria-label={t('list.loading')} className={gridClass}>
          {[0, 1, 2].map((n) => (
            <Card key={n}>
              <CardHeader>
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardFooter>
                <Skeleton className="h-8 w-20 rounded-full" />
              </CardFooter>
            </Card>
          ))}
        </section>
      ) : null}

      {failed ? (
        <ErrorState
          title={t('list.errorTitle')}
          description={failed.error?.message}
          onRetry={() => {
            void projects.refetch();
            void orders.refetch();
          }}
        />
      ) : null}

      {ready && !failed && ready.length === 0 ? (
        <EmptyState
          title={t('list.empty')}
          description={canStart ? t('list.emptyHelp') : t('list.emptyReadOnly')}
        />
      ) : null}

      {!failed && listed.length > 0 ? (
        <ul className={gridClass}>
          {listed.map((project) => (
            <li key={project.id}>
              {/* The title link stretches over the whole card; "View" is a visual affordance. */}
              <Card className="relative h-full transition-shadow duration-150 ease-standard focus-within:shadow-overlay hover:shadow-overlay">
                <CardHeader>
                  <CardTitle asChild>
                    <h2 className="break-words">
                      <Link
                        to={`${base}/projects/${project.id}`}
                        className="text-heading no-underline after:absolute after:inset-0 after:rounded-lg"
                      >
                        {project.name}
                      </Link>
                    </h2>
                  </CardTitle>
                  <CardDescription>
                    {project.due_at
                      ? t('list.due', { date: formatDateTime(project.due_at) })
                      : t('list.created', { date: formatDate(project.created_at) })}
                  </CardDescription>
                </CardHeader>
                <CardFooter className="mt-auto flex flex-wrap items-center justify-between gap-2">
                  {(() => {
                    const status = customerStatus(project, orderOf.get(project.id) ?? null);
                    return (
                      <StatusBadge tone={CUSTOMER_STATUS_TONE[status]}>
                        {t(`status.${status}`)}
                      </StatusBadge>
                    );
                  })()}
                  <Button asChild variant="secondary" size="sm">
                    <span aria-hidden="true">{t('list.view')}</span>
                  </Button>
                </CardFooter>
              </Card>
            </li>
          ))}
        </ul>
      ) : null}
    </PageLayout>
  );
}
